/**
 * Settings → Topics (web.md §4, fix 1 `target` icon, fix 7 Scoring block; models.md §5.0, §12).
 *
 * Editor: Name (every model reads it; Jev reads only it), a Paraphrases block for on-device models (one
 * per line, "5 of 16"), an optional Description, icon, colour. Under every text field a "Read by" line
 * names the switched-on models that read it, with a note when one of them takes less than the field holds.
 * Then the Scoring block — rows `Model · Score ≥ N · Action · Value` that are scoring rules of this topic
 * (rules.*), kept above the general ladder and highest N first. No importance or threshold fields.
 */

import { toDigesterError, type Engine, type Model, type ModelCatalog, type Rule, type RuleId, type Tag, type Topic, type TopicField, type TopicId } from '@digester/core';
import { useState } from 'react';
import { useFolders } from '../../app/hooks.js';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { IconField } from '../../icons/IconField.js';
import { Icon } from '../../icons/Icon.js';
import { Field, FormGrid, Section, Select, TextArea } from '../../ui/form.js';
import { Tool } from '../../ui/Tool.js';
import { useScoringRules, useTags } from '../rules/data.js';
import { useConnectors } from '../mcps/connectors.js';
import { entryOf, shortModelName, useCatalog, useModels } from '../models/data.js';
import { actionsOf, type DraftAction } from '../rules/model.js';
import { orderWithTopicBlock, rowsOfTopic, sameOrder, topicCondition, topicRowName } from '../rules/scoring.js';
import '../rules/rules.css';
import { EditorEmpty, EditorFrame, useDraft, type ItemsResult, type SettingsCategory, type SettingsEditorProps } from '../shell/index.js';

export interface ScoringRowDraft {
  ruleId: string | null;
  /** The model whose score the row tests ('' = the shown scores). */
  model: string;
  min: number;
  action: DraftAction;
}

export interface TopicDraft {
  name: string;
  hint: string;
  /** Paraphrases, one per line (as typed; blank lines are dropped on save). */
  phrases: string;
  icon: string;
  color: string;
  enabled: boolean;
  rows: ScoringRowDraft[];
}

const NEW_TOPIC: TopicDraft = { name: '', hint: '', phrases: '', icon: 'target', color: '#b3a3ed', enabled: true, rows: [] };

/** The paraphrases of the textarea: one per line, trimmed, blank lines dropped (duplicates are the engine's to refuse). */
export function phrasesOf(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
}

/** The most paraphrases a topic keeps (`topics.max_phrases`, which is also the on-device model's own limit). */
function maxPhrases(catalog: ModelCatalog | undefined): number {
  const limits = (catalog?.entries ?? []).map((e) => e.topicFields.limits.phrases ?? 0);
  return Math.max(16, ...limits);
}

/** Models that are switched on and runnable on this plan, in model order. */
function activeModels(models: readonly Model[]): Model[] {
  return models.filter((m) => m.enabled && m.entitled);
}

/**
 * "Read by: Multilingual E5 Small" / "Not read by any active model", plus a note for every reading model
 * that takes less than the field holds ("Jev reads the first 80 characters") — models.md §5.0.
 */
function ReadBy({ field, models, catalog, length, count }: { field: TopicField; models: readonly Model[]; catalog: ModelCatalog | undefined; length: number; count?: number }) {
  const readers = activeModels(models).filter((m) => field === 'name' || m.options.topicFields.includes(field));
  const notes: string[] = [];
  for (const m of readers) {
    const limits = entryOf(catalog, m.id)?.topicFields.limits;
    if (!limits) continue;
    if (field === 'name' && length > limits.nameChars) notes.push(`${m.name} reads the first ${limits.nameChars} characters.`);
    if (field === 'description' && limits.descriptionChars !== null && length > limits.descriptionChars) notes.push(`${m.name} reads the first ${limits.descriptionChars.toLocaleString('en')} characters.`);
    if (field === 'phrases') {
      if (limits.phrases !== null && (count ?? 0) > limits.phrases) notes.push(`${m.name} reads the first ${limits.phrases} paraphrases.`);
      if (limits.phraseChars !== null && length > limits.phraseChars) notes.push(`${m.name} reads the first ${limits.phraseChars} characters of each.`);
    }
  }
  return (
    <p className="field-help read-by" data-read-by={field}>
      {readers.length ? `Read by: ${readers.map((m) => m.name).join(', ')}.` : 'Not read by any active model.'}
      {notes.length ? ` ${notes.join(' ')}` : ''}
    </p>
  );
}

function useItems(): ItemsResult {
  const q = useEngineQuery(qk.topics(), (engine) => engine.topics.list());
  const { rules } = useScoringRules();
  return {
    items: (q.data ?? []).map((t) => {
      const n = rowsOfTopic(rules, t.id).length;
      const p = t.phrases.length;
      const subtitle = t.hint || [p ? `${p} paraphrase${p === 1 ? '' : 's'}` : 'Name only', n ? `${n} scoring rule${n === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
      return { id: t.id, name: t.name, icon: t.icon, subtitle, enabled: t.enabled };
    }),
    isLoading: q.isLoading,
    error: q.error ?? null,
  };
}

function actionText(action: DraftAction, folderName: (id: string) => string): string {
  if (action.type === 'move') return folderName(action.folderId);
  if (action.type === 'delete') return 'Delete';
  return `${action.type === 'add-tag' ? '+' : '−'}${action.tag}`;
}

function rowsOf(rules: readonly Rule[], topicId: string, tags: readonly Tag[]): ScoringRowDraft[] {
  return rowsOfTopic(rules, topicId).map((r) => ({
    ruleId: r.ruleId,
    model: r.model ?? '',
    min: r.min,
    action:
      r.action.type === 'move'
        ? { type: 'move', folderId: r.action.folderId }
        : r.action.type === 'delete'
          ? { type: 'delete' }
          : { type: r.action.type, tag: tags.find((t) => t.id === (r.action as { tagId: string }).tagId)?.name ?? (r.action as { tagId: string }).tagId },
  }));
}

/** Persist the Scoring block: archive removed rows, update changed ones, create new ones, then fix positions. */
async function saveRows(engine: Engine, topic: Topic, rows: readonly ScoringRowDraft[], tags: readonly Tag[], folderName: (id: string) => string): Promise<void> {
  const before = rowsOfTopic(await engine.rules.list('scoring'), topic.id);
  const keep = new Set(rows.map((r) => r.ruleId).filter((id): id is string => id !== null));
  const ensureTag = async (name: string) => (await engine.tags.create({ name, icon: 'tag' as never })).id;
  for (const old of before) if (!keep.has(old.ruleId)) await engine.rules.archive(old.ruleId);
  const sorted = [...rows].sort((a, b) => b.min - a.min);
  const ids: RuleId[] = [];
  for (const row of sorted) {
    const [action] = await actionsOf([row.action], tags, ensureTag);
    if (!action) continue;
    const fields = {
      name: topicRowName(topic.name, row.min, actionText(row.action, folderName)),
      icon: topic.icon,
      condition: topicCondition(topic.id as TopicId, row.min, row.model || null),
      actions: [action],
    };
    const old = row.ruleId ? before.find((b) => b.ruleId === row.ruleId) : undefined;
    if (old) {
      const changed = old.min !== row.min || (old.model ?? '') !== row.model || JSON.stringify(old.action) !== JSON.stringify(action);
      const current = (await engine.rules.list('scoring')).find((r) => r.id === old.ruleId);
      if (changed || current?.name !== fields.name || current.icon !== fields.icon) await engine.rules.update(old.ruleId, fields);
      ids.push(old.ruleId);
    } else {
      const created = await engine.rules.create({ kind: 'scoring', enabled: true, triggerFolderId: null, ...fields });
      ids.push(created.id);
    }
  }
  const live = (await engine.rules.list('scoring')).filter((r) => r.archivedAt === null);
  const order = orderWithTopicBlock(live, topic.id, ids);
  if (!sameOrder(order, live.map((r) => r.id))) await engine.rules.reorder('scoring', order);
}

function TopicEditor({ recordId, duplicateOf }: SettingsEditorProps) {
  const q = useEngineQuery(qk.topics(), (engine) => engine.topics.list());
  const { rules, isLoading } = useScoringRules();
  const tags = useTags();
  if (!recordId) return <EditorEmpty />;
  if (q.isLoading || isLoading) return <EditorEmpty text="Loading…" />;
  const topics = q.data ?? [];
  if (recordId === 'new') {
    const from = duplicateOf ? topics.find((t) => t.id === duplicateOf) : undefined;
    const initial: TopicDraft = from
      ? { name: `${from.name} copy`, hint: from.hint, phrases: from.phrases.join('\n'), icon: from.icon, color: from.color, enabled: true, rows: rowsOf(rules, from.id, tags).map((r) => ({ ...r, ruleId: null })) }
      : NEW_TOPIC;
    return <TopicForm topic={null} initial={initial} />;
  }
  const topic = topics.find((t) => t.id === recordId);
  if (!topic) return <EditorEmpty />;
  return (
    <TopicForm
      topic={topic}
      initial={{ name: topic.name, hint: topic.hint, phrases: topic.phrases.join('\n'), icon: topic.icon, color: topic.color, enabled: topic.enabled, rows: rowsOf(rules, topic.id, tags) }}
    />
  );
}

function TopicForm({ topic, initial }: { topic: Topic | null; initial: TopicDraft }) {
  const engine = useEngine();
  const tags = useTags();
  const folders = useFolders();
  const { models } = useModels();
  const catalog = useCatalog();
  const connectors = useConnectors();
  const [issues, setIssues] = useState<Record<string, string>>({});
  const folderName = (id: string): string => folders.find((f) => f.id === id)?.name ?? id;
  const draft = useDraft<TopicDraft>({
    initial,
    isNew: topic === null,
    onSave: async (v) => {
      setIssues({});
      const input = { name: v.name.trim(), hint: v.hint.trim(), phrases: phrasesOf(v.phrases), icon: v.icon as never, color: v.color as never, enabled: v.enabled };
      let saved: Topic;
      try {
        saved = topic ? await engine.topics.update(topic.id, input) : await engine.topics.create(input);
      } catch (error) {
        const e = toDigesterError(error);
        const found: Record<string, string> = {};
        for (const issue of e.details?.issues ?? []) {
          const key = issue.path.split('.')[0] ?? '';
          found[key] = found[key] ? `${found[key]} ${issue.message}` : issue.message;
        }
        setIssues(found);
        throw e;
      }
      if (JSON.stringify(v.rows) !== JSON.stringify(initial.rows)) await saveRows(engine, saved, v.rows, tags, folderName);
      return saved.id;
    },
  });
  const v = draft.value;
  const setRow = (i: number, row: ScoringRowDraft): void => draft.set((d) => ({ ...d, rows: d.rows.map((r, j) => (j === i ? row : r)) }));
  const phrases = phrasesOf(v.phrases);
  const max = maxPhrases(catalog);
  const longest = phrases.reduce((n, p) => Math.max(n, [...p].length), 0);
  const firstModel = activeModels(models)[0]?.id ?? '';
  const hasConnector = (connectors.query?.data?.connectors.length ?? 0) > 0;
  const onDeviceName = models.find((m) => m.kind === 'onnx-embedding')?.name ?? 'The on-device model';

  return (
    <EditorFrame title={v.name.trim() || (topic ? topic.name : 'New topic')} icon={v.icon} enabled={{ checked: v.enabled, onChange: (enabled) => draft.set((d) => ({ ...d, enabled })) }}>
      <Section title="Topic">
        <Field label="Name" name="name" value={v.name} required maxLength={80} placeholder="e.g. Local AI tools" onChange={(name) => draft.set((d) => ({ ...d, name }))} />
        <p className="field-help">Every model reads the name, and Jev reads only the name: make it say what you want to read about.</p>
        <ReadBy field="name" models={models} catalog={catalog} length={[...v.name.trim()].length} />
        {issues.name ? <p className="field-issue" role="alert">{issues.name}</p> : null}
        <div className="topic-phrases" data-phrases>
          <label className="field">
            <span>
              Paraphrases · one per line <b className="phrase-count" data-phrase-count>{phrases.length} of {max}</b>
            </span>
            <textarea name="phrases" rows={Math.min(10, Math.max(4, phrases.length + 1))} value={v.phrases} placeholder={'e.g. small language models running on a laptop\nopen-weight model releases'} onChange={(e) => draft.set((d) => ({ ...d, phrases: e.target.value }))} />
          </label>
          <p className="field-help">Short phrases, each naming one facet of the topic. The on-device model matches from these; Jev reads the name.</p>
          {phrases.length === 0 ? (
            <div className="editor-note phrases-empty" data-phrases-empty>
              No paraphrases — {onDeviceName === 'The on-device model' ? 'the on-device model' : `${onDeviceName}`} matches this topic from its name only (weaker).
              {hasConnector ? (
                <>
                  {' '}
                  Ask your assistant: use the prompt <em>write_topic_phrases</em>.
                </>
              ) : null}
            </div>
          ) : null}
          <ReadBy field="phrases" models={models} catalog={catalog} length={longest} count={phrases.length} />
          {issues.phrases ? (
            <p className="field-issue" role="alert" data-issue="phrases">
              {issues.phrases}
            </p>
          ) : null}
        </div>
        <TextArea label="Description · optional" name="hint" value={v.hint} rows={4} maxLength={2000} placeholder="What belongs in this topic?" onChange={(hint) => draft.set((d) => ({ ...d, hint }))} />
        <ReadBy field="description" models={models} catalog={catalog} length={[...v.hint.trim()].length} />
        {issues.hint ? <p className="field-issue" role="alert">{issues.hint}</p> : null}
        <FormGrid>
          <IconField label="Icon" name="icon" value={v.icon} onChange={(icon) => draft.set((d) => ({ ...d, icon }))} />
          <Field label="Color" name="color" type="color" value={v.color} onChange={(color) => draft.set((d) => ({ ...d, color }))} />
        </FormGrid>
      </Section>
      <Section title="Scoring">
        <div className="scoring-rows">
          {v.rows.map((row, i) => (
            <div className="scoring-row with-model" key={i} data-scoring-row={i}>
              <Select
                label="Model"
                name={`s${i}-model`}
                value={row.model}
                options={[['', 'Shown scores'] as const, ...models.map((m) => [m.id as string, shortModelName(m.id, models, catalog)] as const)]}
                onChange={(model) => setRow(i, { ...row, model })}
              />
              <Field label="Score ≥" name={`s${i}-min`} type="number" min={0} max={100} required value={row.min} onChange={(min) => setRow(i, { ...row, min: Number(min) })} />
              <Select
                label="Action"
                name={`s${i}-type`}
                value={row.action.type}
                options={[['move', 'Move to'], ['add-tag', 'Add tag'], ['remove-tag', 'Remove tag'], ['delete', 'Delete']]}
                onChange={(type) => setRow(i, { ...row, action: type === 'move' ? { type, folderId: 'priority' } : type === 'delete' ? { type } : { type, tag: 'tag' in row.action ? row.action.tag : '' } })}
              />
              {row.action.type === 'move' ? (
                <Select label="Folder" name={`s${i}-value`} value={row.action.folderId} options={folders.map((f) => [f.id, f.name] as const)} onChange={(folderId) => setRow(i, { ...row, action: { type: 'move', folderId } })} />
              ) : row.action.type === 'delete' ? (
                <span className="action-note">Source statistics are preserved</span>
              ) : (
                <label className="field">
                  <span>Tag</span>
                  <input
                    type="text"
                    name={`s${i}-value`}
                    required
                    maxLength={80}
                    list={`s${i}-tags`}
                    value={row.action.tag}
                    onChange={(e) => setRow(i, { ...row, action: { type: (row.action as { type: 'add-tag' | 'remove-tag' }).type, tag: e.target.value } })}
                  />
                  <datalist id={`s${i}-tags`}>
                    {tags.map((t) => (
                      <option key={t.id} value={t.name} />
                    ))}
                  </datalist>
                </label>
              )}
              <Tool icon="x" label="Remove rule" onClick={() => draft.set((d) => ({ ...d, rows: d.rows.filter((_, j) => j !== i) }))} />
            </div>
          ))}
        </div>
        <button
          type="button"
          className="add-line"
          onClick={() =>
            draft.set((d) => ({
              ...d,
              rows: [...d.rows, { ruleId: null, model: firstModel, min: d.rows.length ? Math.max(0, Math.min(...d.rows.map((r) => r.min)) - 20) : 80, action: { type: 'move', folderId: 'priority' } }],
            }))
          }
        >
          <Icon name="plus" /> Add rule
        </button>
        <p className="field-help">Otherwise the general ladder in Models applies. Rules here run before the ladder, the highest score first; a rule naming a model waits for that model’s score.</p>
      </Section>
    </EditorFrame>
  );
}

export const category: SettingsCategory = {
  id: 'topics',
  name: 'Topics',
  icon: 'target',
  useItems,
  Editor: TopicEditor,
  newLabel: 'New topic',
  duplicate: true,
  archive: (engine, item) => engine.topics.archive(item.id as TopicId),
  archiveNote: 'Existing scores and message history stay. Its scoring rules stop matching until the topic is restored.',
};
